import { useAuthenticatedUser } from './user.hooks'

/**
 * What the current user may do to the things in their scope.
 *
 * `disabledReason` follows MethodCard's contract: present means the action is out of reach, and the
 * text is what the user is told. Controls stay on screen when it is set — seeing that a capability
 * exists is half of knowing to ask an owner for it.
 */
export type ScopePermissions = {
  isOwner: boolean
  canCreate: boolean
  canEdit: boolean
  canArchive: boolean
  disabledReason?: string
}

export const OWNER_ONLY_REASON = 'Only a scope owner can make this change. Ask an owner of your scope to do it.'

/**
 * Pubstack staff read as owners here because `getScopeRole()` returns `'pubstack'` for them, never
 * `'owner'` — which is why the raw check is spelled `=== 'owner' || isAdmin` everywhere it appears.
 *
 * The three verbs are equal today and deliberately kept apart: they are where a future "archiving is
 * owner-only but editing is not" lands, without revisiting every call site.
 */
export const useScopePermissions = (): ScopePermissions => {
  const user = useAuthenticatedUser()
  const isOwner = user.getScopeRole() === 'owner' || user.isAdmin
  return {
    isOwner,
    canCreate: isOwner,
    canEdit: isOwner,
    canArchive: isOwner,
    disabledReason: isOwner ? undefined : OWNER_ONLY_REASON,
  }
}
